/**
 * Clear Fusion canvas — Workbench "Clear Canvas" parity for the sandbox.
 * Removes live Cytoscape elements, client-saved snapshot, intake queue, and
 * Run-rail jobs. Turns demo seed OFF so the board is blank for the next case.
 */
import type cytoscape from 'cytoscape';
import { clearFusionCase } from './fusionCaseStore';
import { useFusionStore } from '../stores/useFusionStore';
import { useIntakeStore } from '../stores/useIntakeStore';
import { useBankImportStore } from '../stores/useBankImportStore';

const cy = (): cytoscape.Core | undefined => {
  const c = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
  return c && !c.destroyed() ? c : undefined;
};

export type ClearFusionOpts = {
  /** Also reset Build cards / Journal client log (default true). */
  resetIntakeBoard?: boolean;
};

export function clearFusionCanvas(opts: ClearFusionOpts = {}): void {
  const resetIntakeBoard = opts.resetIntakeBoard !== false;
  const c = cy();
  if (c) {
    try {
      c.elements().remove();
    } catch { /* destroyed mid-clear */ }
  }

  clearFusionCase();

  const fusion = useFusionStore.getState();
  fusion.select(null);
  fusion.clearJobs();
  fusion.closeRadial();
  fusion.setPb(null);
  fusion.setPlaying(false);
  fusion.setStep(1);
  fusion.setShowDemo(false);

  const intake = useIntakeStore.getState();
  const keepCaseId = intake.caseId;
  if (resetIntakeBoard) {
    intake.reset();
    if (keepCaseId) intake.setCaseId(keepCaseId);
  } else {
    intake.saveUserElements([]);
    intake.drainPendingNodes();
  }

  useBankImportStore.getState().clearDossier();
}

/** Confirm when there is something worth wiping. */
export function confirmClearFusionCanvas(): boolean {
  const c = cy();
  const n = c && !c.destroyed() ? c.elements().length : 0;
  const pending = useIntakeStore.getState().pendingNodes.length
    + useIntakeStore.getState().userElements.length
    + useIntakeStore.getState().cards.length;
  if (n === 0 && pending === 0) return true;
  return window.confirm(
    'Clear the Fusion canvas?\n\nThis removes all nodes, edges, Build cards, and the local Save snapshot. Demo data turns off.',
  );
}
